// UniversalSmellsPanel — Gate G3.7 Phase 6 (plan §8.1).
//
// Collapsible card rendered at the top of the G3.7 panel showing
// the 8 universal smells the deterministic detector emitted on the
// post-G3 SRD. Each smell row carries:
//
//   * ✓ / ✗ glyph (clean / hits)
//   * smell id + title (U-1 Vague verbs, U-2 Unbounded quantities, …)
//   * occurrence count
//   * "show occurrences" expander listing the line excerpts
//
// Smells are persisted as part of the depth-findings envelope —
// see ``ai_alm.services.srd_smells`` for the detector and
// ``UniversalSmellOccurrence`` in ``frontend/src/types/depth.ts``
// for the wire shape.
//
// Plan §8.4 verification step (manual): the architect should be
// able to expand the panel, see U-1 through U-8 listed, and click
// any non-zero row to confirm the occurrence excerpts match what
// they'd find in srd.md by hand.

import { useState } from 'react'
import { Check, ChevronDown, ChevronRight, X } from 'lucide-react'

import type { UniversalSmellOccurrence } from '@/types/depth'

// --------------------------------------------------------------------------- //
// Public props
// --------------------------------------------------------------------------- //
export interface UniversalSmellsPanelProps {
  /** Smell rows from the depth-findings envelope. */
  smells: UniversalSmellOccurrence[]
  /**
   * When true the panel is rendered collapsed. The architect can
   * expand it inline. Defaults to true so the G3.7 panel doesn't
   * dump a 200-line smells dump above the per-finding cards.
   */
  defaultCollapsed?: boolean
}

// --------------------------------------------------------------------------- //
// Component
// --------------------------------------------------------------------------- //
export function UniversalSmellsPanel({
  smells,
  defaultCollapsed = true,
}: UniversalSmellsPanelProps) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed)
  const [expandedRow, setExpandedRow] = useState<string | null>(null)

  if (!smells || smells.length === 0) {
    return null
  }

  const cleanCount = smells.filter((s) => (s.count ?? 0) === 0).length
  const hitCount = smells.length - cleanCount
  const allClean = hitCount === 0

  const summary = allClean
    ? `Universal smells · all ${smells.length} clean`
    : `Universal smells · ${hitCount} of ${smells.length} have findings`

  return (
    <div className="rounded-lg border border-slate-700/60 bg-slate-950/40 mb-4">
      <button
        type="button"
        onClick={() => setCollapsed((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left text-xs"
        aria-expanded={!collapsed}
      >
        {collapsed ? (
          <ChevronRight className="h-3.5 w-3.5 text-slate-400 shrink-0" />
        ) : (
          <ChevronDown className="h-3.5 w-3.5 text-slate-400 shrink-0" />
        )}
        <span className="font-semibold text-slate-200">{summary}</span>
        <span
          className={`ml-auto inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${
            allClean
              ? 'border-emerald-700/50 bg-emerald-950/40 text-emerald-300'
              : 'border-amber-700/50 bg-amber-950/40 text-amber-200'
          }`}
        >
          {allClean ? (
            <>
              <Check className="h-3 w-3" /> all clean
            </>
          ) : (
            <>
              <X className="h-3 w-3" /> {hitCount} hits
            </>
          )}
        </span>
      </button>

      {!collapsed && (
        <div className="border-t border-slate-800/60 px-3 py-2 space-y-1">
          {smells.map((smell) => {
            const count = smell.count ?? 0
            const clean = count === 0
            const hasExamples = (smell.examples?.length ?? 0) > 0
            const isExpanded = expandedRow === smell.id
            return (
              <div
                key={smell.id}
                className="rounded-md border border-slate-800/40 bg-slate-900/30 px-2.5 py-1.5"
              >
                <button
                  type="button"
                  onClick={() => {
                    if (!hasExamples || clean) return
                    setExpandedRow(isExpanded ? null : smell.id)
                  }}
                  className="w-full flex items-center gap-2 text-left text-[12px]"
                  disabled={clean || !hasExamples}
                  title={
                    clean
                      ? 'No occurrences detected'
                      : hasExamples
                      ? 'Click to show occurrences'
                      : 'No examples persisted'
                  }
                >
                  {clean ? (
                    <Check className="h-3 w-3 text-emerald-400 shrink-0" />
                  ) : (
                    <X className="h-3 w-3 text-amber-400 shrink-0" />
                  )}
                  <span className="font-mono text-[10.5px] text-slate-400 shrink-0">
                    {smell.id}
                  </span>
                  <span className="font-semibold text-slate-200 truncate">
                    {smell.title}
                  </span>
                  <span className="ml-auto inline-flex items-center gap-2 shrink-0">
                    <span
                      className="alm-chip text-[10px]"
                      title={`Severity: ${smell.severity}`}
                    >
                      {smell.severity}
                    </span>
                    <span
                      className={`font-mono text-[10.5px] tabular-nums ${
                        clean ? 'text-slate-500' : 'text-amber-300'
                      }`}
                    >
                      {count}× hit{count === 1 ? '' : 's'}
                    </span>
                    {!clean && hasExamples && (
                      <span className="text-slate-500">
                        {isExpanded ? (
                          <ChevronDown className="h-3 w-3" />
                        ) : (
                          <ChevronRight className="h-3 w-3" />
                        )}
                      </span>
                    )}
                  </span>
                </button>
                {isExpanded && hasExamples && (
                  <ul className="mt-2 ml-5 space-y-1 text-[11.5px] text-slate-300">
                    {(smell.examples ?? []).map((excerpt, i) => (
                      <li
                        key={i}
                        className="font-mono leading-snug border-l-2 border-amber-700/40 pl-2"
                      >
                        {excerpt}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
